import Button from '@mui/material/Button';
import React from 'react';
import { useGetOrganisationLocations } from '../../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../../app/CurrentOrganisationProvider';
import { getLocationAvatar } from '../../../../../../components/Avatar/Location';
import { DataErrorHandler } from '../../../../../../components/ErrorHandler';
import { SelectableItem, SelectionList } from '../../../../../../components/SelectionList';
import { useWizard } from '../../../../../../design-system';
import { getAddressAsString } from '../../../../../../utils/locations/address';
import { normalise } from '../../../../../../utils/request';
import { LocationDetails } from '../types';
import { CreateLocationDialog } from './CreateLocationDialog';

export const LocationStep: React.FC = () => {
  const { setState, prevStep, nextStep } = useWizard();

  const { organisation } = useCurrentOrganisation();

  const organisationSlug: string = organisation?.slug ?? '';

  const { loading, error, data, refetch } = useGetOrganisationLocations({ orgSlug: organisationSlug });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);

  const onLocationSelect = React.useCallback(
    (location: LocationDetails) => {
      setState({ location });
      nextStep();
    },
    [setState, nextStep],
  );

  const onLocationCreated = React.useCallback(
    (location: LocationDetails) => {
      setIsCreateDialogOpen(false);
      refetch().catch(() => null);
      onLocationSelect(location);
    },
    [refetch, onLocationSelect],
  );

  const locations = React.useMemo(() => (data ? normalise(data) : []), [data]);
  const selectableLocations: SelectableItem[] = React.useMemo(
    () =>
      locations.map(({ id, name, address }) => ({
        id,
        title: name,
        subtitle: getAddressAsString(address),
      })),
    [locations],
  );

  const onSelectItem = React.useCallback(
    ({ id }: SelectableItem) => {
      const location = locations.find((candidate) => candidate.id === id);
      if (location) {
        onLocationSelect({ id: location.id, name: location.name, address: location.address });
      }
    },
    [locations, onLocationSelect],
  );

  if (error || (!loading && !data)) {
    return (
      <DataErrorHandler
        description="Unable to load the organisation's locations."
        error={error}
        refetch={refetch}
        type="embedded"
      />
    );
  }

  return (
    <>
      <SelectionList
        description="Each charge point belongs to a location"
        getImage={getLocationAvatar}
        items={selectableLocations}
        label="location"
        loading={loading && !data}
        noItemsDescription="To add a charge point you must first have a location for it to be at"
        onSelectItem={onSelectItem}
        upperActions={
          <Button color="primary" variant="outlined" onClick={() => setIsCreateDialogOpen(true)}>
            Create new location
          </Button>
        }
        lowerActions={
          <Button color="inherit" onClick={prevStep} variant="contained">
            Back
          </Button>
        }
      />
      <CreateLocationDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreated={onLocationCreated}
      />
    </>
  );
};
